import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useCallback, useMemo } from 'react';
import cn from 'classnames';
import { useIntentBehavior } from '../../providers/IntentBehavior';
import { useBiAction } from '../BiTracking';
import { useBiContextGetter } from '../BiTracking/BiContext';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { Tooltip } from '../Tooltip';
import typographyStyles from '../Typography/Typography.module.scss';
import styles from './TextButton.module.scss';
import { GetTextButtonTID } from './TextButton.utils';
/** Renders a button as text with an underline (styled as a link). */ export const TextButton = /*#__PURE__*/ forwardRef(function TextButton(props, ref) {
    const { as, className, children, type, skin = 'primary', hoverSkin, underline = 'always', weight = 'light', variant = 'paragraph1', prefixIcon, suffixIcon, disabled, loading, intent, tooltip, bi, noBi: _noBi, onClick, testId } = props, rest = _object_without_properties(props, [
        "as",
        "className",
        "children",
        "type",
        "skin",
        "hoverSkin",
        "underline",
        "weight",
        "variant",
        "prefixIcon",
        "suffixIcon",
        "disabled",
        "loading",
        "intent",
        "tooltip",
        "bi",
        "noBi",
        "onClick",
        "testId"
    ]);
    const { TextButtonTestIds } = useMemo(()=>GetTextButtonTID(testId), [
        testId
    ]);
    const { getDisabled, getTooltip } = useIntentBehavior();
    const disabledFromIntent = intent ? getDisabled(intent) : false;
    const tooltipFromIntent = intent ? getTooltip(intent) : null;
    const hasTooltip = !!(tooltip === null || tooltip === void 0 ? void 0 : tooltip.title) || !!tooltipFromIntent;
    const biContext = useBiContextGetter();
    const RootElement = as || 'button';
    const logBiAction = useBiAction();
    const handleClick = useCallback((e)=>{
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'text_button',
                entityName: bi.name,
                actionType: 'click',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : ''
                })
            });
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(e, biContext);
    }, [
        bi,
        logBiAction,
        onClick,
        biContext,
        testId
    ]);
    return /*#__PURE__*/ _jsx(ConditionalWrapper, {
        condition: hasTooltip,
        wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, _object_spread_props(_object_spread({
                testId: TextButtonTestIds.Tooltip(testId)
            }, tooltip || {
                placement: 'top',
                title: tooltipFromIntent
            }), {
                children: /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.wrapper, className),
                    "data-testid": TextButtonTestIds.Wrapper(testId),
                    children: children
                })
            })),
        children: /*#__PURE__*/ _jsxs(RootElement, _object_spread_props(_object_spread({
            ref: ref,
            type: type || 'button'
        }, rest), {
            onClick: handleClick,
            disabled: disabledFromIntent || disabled || loading,
            "aria-busy": loading !== null && loading !== void 0 ? loading : false,
            "data-testid": testId,
            className: cn(styles.root, styles["".concat(skin, "Skin")], styles["".concat(underline, "Underline")], hoverSkin && styles["".concat(hoverSkin, "Hover")], typographyStyles["".concat(weight, "Weight")], typographyStyles["".concat(variant, "Variant")], loading && styles.loading, !hasTooltip && className),
            children: [
                prefixIcon,
                /*#__PURE__*/ _jsx("span", {
                    className: loading ? typographyStyles.shimmer : undefined,
                    children: children
                }),
                suffixIcon
            ]
        }))
    });
});
